import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FloatingLabelInput } from '@/shared/components/custom/form-field';
import type { RenameProjectFileInput } from '../types';

interface FileRenameDialogProps {
  open: boolean;
  currentName: string;
  onSubmit: (data: RenameProjectFileInput) => void;
  onCancel: () => void;
}

export function FileRenameDialog({ open, currentName, onSubmit, onCancel }: FileRenameDialogProps) {
  const { t } = useTranslation('projectFiles');
  const { t: tCommon } = useTranslation('common');
  const [name, setName] = useState(currentName);
  const [error, setError] = useState('');

  function handleSubmit() {
    if (!name.trim()) {
      setError(t('form.validation.nameRequired'));
      return;
    }
    onSubmit({ name: name.trim() });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onCancel();
      }}
    >
      <DialogContent data-testid="project-files-rename-dialog">
        <DialogHeader>
          <DialogTitle>{t('file.renameDialogTitle')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <FloatingLabelInput
            id="file-rename"
            label={t('file.renameLabel')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            invalid={!!error}
            data-testid="project-files-rename-dialog-input"
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {tCommon('actions.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} data-testid="project-files-rename-dialog-submit">
            {t('item.rename')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
